
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { Button } from "@/components/ui/button";

export default function FAQ() {
  const navigate = useNavigate();

  useEffect(() => {
    // Add comprehensive SEO meta tags
    document.title = "FAQ - Soundope 2025 | Real Answers, Zero Fake Promises";

    const metaTags = [
      { type: 'name', key: 'description', content: "Free feedback forever. Free playlist submission. Paid from $1. Real curators, real playlists, real answers. No bots, no fake guarantees." },
      { type: 'name', key: 'keywords', content: "soundope faq, free music feedback, playlist submission cost, black wings elite, real curators, music promotion" },
      { type: 'name', key: 'robots', content: 'index, follow' },

      // Open Graph
      { type: 'property', key: 'og:title', content: 'FAQ - Soundope 2025' },
      { type: 'property', key: 'og:description', content: 'Real answers about free feedback, playlist submission, and music promotion' },
      { type: 'property', key: 'og:type', content: 'website' },
      { type: 'property', key: 'og:url', content: window.location.href },

      // Twitter
      { type: 'name', key: 'twitter:card', content: 'summary' },
      { type: 'name', key: 'twitter:title', content: 'Soundope FAQ' },
      { type: 'name', key: 'twitter:description', content: 'Real answers. Zero fake promises.' },

      // Canonical URL
      { type: 'link', key: 'canonical', content: window.location.origin + window.location.pathname },
    ];

    metaTags.forEach(({ type, key, content }) => {
      if (type === 'link') {
        let tag = document.querySelector(`link[rel="${key}"]`);
        if (tag) {
          tag.setAttribute('href', content);
        } else {
          tag = document.createElement('link');
          tag.setAttribute('rel', key);
          tag.setAttribute('href', content);
          document.head.appendChild(tag);
        }
      } else {
        // For meta tags, 'property' is used for Open Graph, 'name' for standard
        const selector = type === 'property' ? `meta[property="${key}"]` : `meta[name="${key}"]`;
        let tag = document.querySelector(selector);
        if (tag) {
          tag.setAttribute('content', content);
        } else {
          tag = document.createElement('meta');
          tag.setAttribute(type, key);
          tag.setAttribute('content', content);
          document.head.appendChild(tag);
        }
      }
    });
  }, []);

  return (
    <div className="min-h-screen px-4 py-16 pb-32">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-light mb-4 text-[#d0d0d0]">
            FAQ – Soundope 2025
          </h1>
          <p className="text-lg text-[#909090]">
            (Real answers. Zero fake promises.)
          </p>
        </div>

        {/* FAQ Content */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 mb-12">
          <div className="space-y-8 text-[#909090]">
            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                1. Is feedback really free?
              </h2>
              <p className="text-base leading-relaxed">
                Yes. Upload a track → real people play it → real praise or scathe. 100% free, forever. No bots, no AI.
              </p>
            </div>

            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                2. Is playlist submission free?
              </h2>
              <p className="text-base leading-relaxed">
                Yes. Every track you upload automatically gets submitted to our in-house playlists at no cost. Real curators decide.
              </p>
            </div>

            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                3. What are the paid playlist options?
              </h2>
              <p className="text-base leading-relaxed mb-3">
                Optional fast-lane to individual curators:
              </p>
              <ul className="text-base leading-relaxed space-y-1 ml-6">
                <li>• $1 → smaller / niche playlists</li>
                <li>• $2–$3 → mid-tier</li>
                <li>• $4 → top-tier</li>
              </ul>
              <p className="text-base leading-relaxed mt-3">
                Still the lowest prices in the game.
              </p>
            </div>

            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                4. Are the playlists real?
              </h2>
              <p className="text-base leading-relaxed">
                100%. Every curator is a vetted human. You can click their profile and see their actual Spotify/YouTube playlists. Zero bots, zero fake accounts.
              </p>
            </div>

            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                5. Can you guarantee streams, followers, or placements?
              </h2>
              <p className="text-base leading-relaxed">
                No. We don't do fake guarantees. If a curator loves your track, they add it. If not, you get honest feedback instead. Real taste, real results, no lies.
              </p>
            </div>

            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                6. What genres do you accept?
              </h2>
              <p className="text-base leading-relaxed">
                Everything. We especially love niche: trap metal, phonk, screamo, crunkcore, hyperpop, blackgaze, witch house, emo rap, scenecore… but all genres are welcome.
              </p>
            </div>

            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                7. What is Black Wings Elite?
              </h2>
              <p className="text-base leading-relaxed">
                $120 one-time or $99/mo. I personally hand-submit your 3 tracks to every active top curator in the vault with a direct note. No stream guarantees, but you skip the line and get maximum human exposure.
              </p>
            </div>

            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                8. Can I sell my tracks here?
              </h2>
              <p className="text-base leading-relaxed">
                Yes. Enable "sell this track" on upload → fans can buy direct from the player.
              </p>
            </div>

            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                9. How do I earn credits without paying?
              </h2>
              <p className="text-base leading-relaxed">
                Leave honest praise/scathe on other tracks. Good reviews = free credits for paid submissions.
              </p>
            </div>

            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                10. Refund policy?
              </h2>
              <ul className="text-base leading-relaxed space-y-2">
                <li>• Free submissions: nothing to refund</li>
                <li>• Paid submissions: full refund if the curator doesn't respond within 7 days</li>
                <li>• Black Wings Elite: full refund anytime if you're not satisfied</li>
              </ul>
            </div>

            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                11. Why is Soundope cheaper than SubmitHub, Groover, Playlist Push, etc.?
              </h2>
              <p className="text-base leading-relaxed">
                Because the money goes straight to real curators instead of middlemen and canvas farms.
              </p>
            </div>

            <div>
              <h2 className="text-lg md:text-xl font-medium text-[#d0d0d0] mb-3">
                12. I'm a curator – how do I join?
              </h2>
              <p className="text-base leading-relaxed">
                Email <a href="mailto:jknoedler@soundope.com" className="text-[#a0a0a0] hover:text-[#b0b0b0] underline">jknoedler@soundope.com</a> with your playlist links. Highest payout in the game + bonuses. We reply in 24h.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Section */}
        <div className="neuro-base rounded-3xl p-8 md:p-12 text-center mb-12">
          <p className="text-lg text-[#909090] mb-4">
            Still got questions? Ask. We actually read the inbox.
          </p>
          <p className="text-xl mb-6">
            <a href="mailto:support@soundope.com" className="text-[#b0b0b0] hover:text-[#d0d0d0] underline">
              support@soundope.com
            </a>
          </p>
          <p className="text-base text-[#808080]">
            Dope Sounds, Soundope
          </p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            onClick={() => navigate(createPageUrl("Upload"))}
            className="neuro-base active:neuro-pressed rounded-3xl px-12 py-6 text-lg smooth-transition hover:scale-105"
            tooltip="Start uploading your tracks to Soundope"
          >
            <span className="text-[#b0b0b0]">Upload Your Track</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
